import { nextTick, onBeforeUnmount, ref } from "vue";

// Plain-language explanations for specs shoppers may not recognise. Opened in
// a small floating window when the info button next to the label is clicked.
const specExplanations: Record<string, string> = {
  Polarized:
    "Polarized lenses have a built-in filter that cuts blinding glare bouncing off water, snow and roads — for sharper, more comfortable vision in bright sun.",
  "Adjustable nosepads":
    "Bendable pads on the bridge let you fine-tune the fit, so the frame sits level and won't slide down your nose.",
  "RX-ABLE":
    "Prescription-ready. An optical shop can pop out the lenses and fit this frame with your own prescription lenses.",
};

export function useSpecPopover() {
  const specInfo = (name: string) => specExplanations[name] || "";

  // Split a label so the LAST word and the info icon can be kept together in a
  // `white-space: nowrap` span. This stops the icon from ever wrapping onto a
  // line by itself when the label wraps — at any screen size. `head` keeps its
  // trailing space (the only place a line break may occur).
  const specLabelParts = (name: string) => {
    const t = String(name ?? "").trim();
    const i = t.lastIndexOf(" ");
    return i === -1
      ? { head: "", tail: t }
      : { head: t.slice(0, i + 1), tail: t.slice(i + 1) };
  };

  // Click-to-open explanation popover. Teleported to <body> so it is never
  // clipped by the accordion panel's overflow, and positioned next to the
  // clicked icon (flips above when there isn't room below).
  const specPopover = ref({
    open: false,
    name: "",
    text: "",
    placement: "bottom" as "top" | "bottom",
    top: 0,
    left: 0,
    arrowLeft: 0,
  });
  const specPopCardRef = ref<HTMLElement | null>(null);

  const placeSpecPopover = (rect: DOMRect) => {
    if (typeof window === "undefined") return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const margin = 12;
    const gap = 10;
    const card = specPopCardRef.value;
    const cardW = card ? card.offsetWidth : 280;
    const cardH = card ? card.offsetHeight : 120;
    const iconCenter = rect.left + rect.width / 2;

    let left = iconCenter - cardW / 2;
    left = Math.max(margin, Math.min(left, vw - cardW - margin));

    let placement: "top" | "bottom" = "bottom";
    let top = rect.bottom + gap;
    if (top + cardH > vh - margin && rect.top - gap - cardH > margin) {
      placement = "top";
      top = rect.top - gap - cardH;
    }

    let arrowLeft = iconCenter - left;
    arrowLeft = Math.max(18, Math.min(arrowLeft, cardW - 18));

    specPopover.value.placement = placement;
    specPopover.value.top = Math.round(top);
    specPopover.value.left = Math.round(left);
    specPopover.value.arrowLeft = Math.round(arrowLeft);
  };

  const dismissSpecPopover = () => closeSpecPopover();
  const onSpecPopoverKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") closeSpecPopover();
  };

  const openSpecPopover = async (name: string, ev: MouseEvent) => {
    const text = specInfo(name);
    if (!text) return;
    const rect = (ev.currentTarget as HTMLElement).getBoundingClientRect();

    // Clicking the active spec again closes it.
    if (specPopover.value.open && specPopover.value.name === name) {
      closeSpecPopover();
      return;
    }

    const wasOpen = specPopover.value.open;
    specPopover.value.name = name;
    specPopover.value.text = text;
    specPopover.value.open = true;
    await nextTick();
    placeSpecPopover(rect);

    if (!wasOpen && typeof window !== "undefined") {
      window.addEventListener("scroll", dismissSpecPopover, true);
      window.addEventListener("resize", dismissSpecPopover);
      window.addEventListener("keydown", onSpecPopoverKey);
    }
  };

  const closeSpecPopover = () => {
    if (!specPopover.value.open) return;
    specPopover.value.open = false;
    if (typeof window !== "undefined") {
      window.removeEventListener("scroll", dismissSpecPopover, true);
      window.removeEventListener("resize", dismissSpecPopover);
      window.removeEventListener("keydown", onSpecPopoverKey);
    }
  };

  onBeforeUnmount(() => {
    closeSpecPopover();
  });

  return {
    specInfo,
    specLabelParts,
    specPopover,
    specPopCardRef,
    openSpecPopover,
    closeSpecPopover,
  };
}
